import React from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import {
  formatApplType,
  getApplTypeInfo,
  ORANGE_BOOK_DATA_FILES_URL,
} from '../utils/applTypeLabels';
import { COLOR_INFO_BG, COLOR_INFO_TEXT } from 'theme/colors';

/** Every tooltip in this feature ends with the same source link (matches TeCodeValue.tsx). */
function buildTooltipContent(bodyText: string): React.ReactNode {
  return (
    <span>
      {bodyText}
      <br />
      Source:{' '}
      <a
        href={ORANGE_BOOK_DATA_FILES_URL}
        target="_blank"
        rel="noopener"
        style={{ color: 'inherit', textDecoration: 'underline' }}>
        Orange Book Data Files
      </a>
    </span>
  );
}

/** Matches InfoTooltip.tsx's own default — one above the modal overlay's zIndex of 10000. */
const DEFAULT_TOOLTIP_Z_INDEX = 10001;

export interface ApplTypeValueProps {
  /**
   * Raw `appl_type` value. Typed `unknown` (not `string | null`) so an
   * unexpected runtime shape at the JSON-API boundary degrades to a
   * coerced string via `String()` instead of throwing — same tolerance
   * TeCodeValue.tsx guarantees for `te_code`.
   */
  code: unknown;
  /** Overrides the tooltip popper's z-index for a call site nested above the base 10000 modal overlay. */
  zIndex?: number;
}

/**
 * Per-value Application Type display: `[code] - [Short name]` with a
 * tooltip carrying the full description. Mirrors TeCodeValue.tsx's pattern
 * (2026-09-23 follow-up feedback) — unlike `te_code`, `appl_type` is always
 * a single code, never a comma-separated combined value, so there's no
 * per-part splitting.
 */
const ApplTypeValue: React.FC<ApplTypeValueProps> = ({
  code,
  zIndex = DEFAULT_TOOLTIP_Z_INDEX,
}) => {
  // Same DOM/tab-order fix as InfoTooltip.tsx/TeCodeValue.tsx: without a
  // `container` next to the trigger, MUI's Popper portals the tooltip (and
  // its Source link) to the end of document.body, unreachable by a plain
  // Tab press.
  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);

  if (code === null || code === undefined || code === '') return <>—</>;
  const raw = typeof code === 'string' ? code : String(code);
  const trimmed = raw.trim();
  if (!trimmed) return <>—</>;

  const info = getApplTypeInfo(trimmed);
  const display = formatApplType(trimmed);

  return (
    <>
      <MuiTooltip
        title={buildTooltipContent(
          info ? info.fullDescription : 'Code not recognized.'
        )}
        // Without this, MUI overwrites the button's accessible name with
        // the tooltip text (aria-label) instead of describing it
        // (aria-describedby) -- the visible short name would never be
        // announced to screen readers.
        describeChild
        arrow
        placement="top"
        slotProps={{
          popper: { style: { zIndex }, container: portalContainer },
        }}>
        <ButtonBase
          component="span"
          disableRipple
          sx={{
            textDecoration: 'underline dotted',
            cursor: 'help',
            font: 'inherit',
            color: 'inherit',
            borderRadius: '2px',
            '&:hover': {
              backgroundColor: COLOR_INFO_BG,
              color: COLOR_INFO_TEXT,
            },
            '&:active': {
              backgroundColor: COLOR_INFO_BG,
            },
            '&:focus-visible': {
              outline: `2px solid ${COLOR_INFO_TEXT}`,
              outlineOffset: '1px',
            },
          }}>
          {display}
        </ButtonBase>
      </MuiTooltip>
      <span ref={setPortalContainer} style={{ display: 'contents' }} />
    </>
  );
};

export default ApplTypeValue;
